import { useState, useEffect, useRef } from 'react';
import { AbstractForm, AbstractFormState, IStateForm } from '../../../common/AbstractForm';
import FormModel from '../../../../utils/FormModel';
import OnPremSiteToSiteVPNStandardConfiguration from '../../../../models/entities/OnPremSiteToSiteVPNStandardConfiguration';

export interface ISiteToSiteStandardVPNConfigurationFormStateForm extends IStateForm {
    option: number;
    ph1EncrypAlgo: string;
    ph1PRFAlgo: string;
    ph1DHGroup: string;
    ph1TimeoutSec: string;
    ph1IKE: string;
    ph2ESPEncryp: string;
    ph2DHGroup: string;
    ph2TimeoutSec: string;
    ph2TimeoutKb: string;
    ph2Authentication: string;
    ph2EnablePFS: string;
}

export interface AbstractStandardSiteToSiteVPNConfigurationForm<T> extends AbstractFormState<T> {
    selectedOption: number;
    standardConfigurationData: any;
}


export const useAbstractStandardSiteToSiteVPNConfigurationForm = (props) => {
    const [state, setState] = useState<AbstractStandardSiteToSiteVPNConfigurationForm<ISiteToSiteStandardVPNConfigurationFormStateForm>>({
        stateForm: {
            option: null,
            ph1EncrypAlgo: null,
            ph1PRFAlgo: null,
            ph1DHGroup: null,
            ph1TimeoutSec: null,
            ph1IKE: null,
            ph2ESPEncryp: null,
            ph2DHGroup: null,
            ph2TimeoutSec: null,
            ph2TimeoutKb: null,
            ph2Authentication: null,
            ph2EnablePFS: null,
        },
        selectedOption: props.selectedOption,
        standardConfigurationData: props.standardConfigurationData
    });

    const prevOptionSelectedRef = useRef<number | null>(null);

    useEffect(() => {
        if (props.mode === 'detail' || props.mode === 'edit') {
            if (props.standardConfig) {
                setState(prevState => {
                    const newState = {
                        ...prevState,
                        stateForm: {
                            ...props.standardConfig
                        }
                    };

                    const standardConfigForm = new OnPremSiteToSiteVPNStandardConfiguration(newState.stateForm);
                    props.onChange(standardConfigForm);

                    return newState;
                });
            }
        }
    }, [])


    useEffect(() => {
        const prevOption = prevOptionSelectedRef.current;
        const currentOption = props.optionSelected;

        const shouldUpdate =
            props.mode === 'create' ||
            (props.mode === 'edit' && prevOption !== null && prevOption !== currentOption);

        if (shouldUpdate && currentOption !== undefined) {
            const updatedStateForm = Object.keys(state.stateForm)
                .filter(fieldName => fieldName !== "option")
                .reduce((acc, fieldName) => {
                    const fieldData = props.standardConfigurationData[fieldName];
                    const selectedOption = fieldData.find(option => option.option === `Option${currentOption}`);
                    acc[fieldName] = selectedOption ? selectedOption.availableOptions[0] : null;
                    return acc;
                }, {} as ISiteToSiteStandardVPNConfigurationFormStateForm);

            updatedStateForm.option = currentOption;

            setState(prevState => {
                const newState = {
                    ...prevState,
                    stateForm: updatedStateForm
                };

                props.onChange(new OnPremSiteToSiteVPNStandardConfiguration(newState.stateForm));
                return newState;
            });
        }

        prevOptionSelectedRef.current = currentOption;
    }, [props.optionSelected, props.mode]);

    const abstractFormInstance = new (class extends AbstractForm<{}, AbstractStandardSiteToSiteVPNConfigurationForm<ISiteToSiteStandardVPNConfigurationFormStateForm>> {
        constructor() {
            super({});
            this.state = state;
        }
    })();

    const [formModel] = useState<FormModel>(
        abstractFormInstance.formModel = new FormModel({
            ph1EncrypAlgo: {
                getValue: () => state.stateForm.ph1EncrypAlgo,
                validation: {
                    required: true,
                },
            },
            ph1PRFAlgo: {
                getValue: () => state.stateForm.ph1PRFAlgo,
                validation: {
                    required: true,
                },
            },
            ph1DHGroup: {
                getValue: () => state.stateForm.ph1DHGroup,
                validation: {
                    required: true,
                },
            },
            ph1IKE: {
                getValue: () => state.stateForm.ph1IKE,
                validation: {
                    required: true,
                },
            },
            ph2ESPEncryp: {
                getValue: () => state.stateForm.ph2ESPEncryp,
                validation: {
                    required: true,
                },
            },
            ph2DHGroup: {
                getValue: () => state.stateForm.ph2DHGroup,
                validation: {
                    required: true,
                },
            },
            ph2TimeoutSec: {
                getValue: () => state.stateForm.ph2TimeoutSec,
                validation: {
                    required: true,
                },
            },
            ph2TimeoutKb: {
                getValue: () => state.stateForm.ph2TimeoutKb,
                validation: {
                    required: true,
                },
            },
            ph2Authentication: {
                getValue: () => state.stateForm.ph2Authentication,
                validation: {
                    required: true,
                },
            },
            ph2EnablePFS: {
                getValue: () => state.stateForm.ph2EnablePFS,
                validation: {
                    required: true,
                },
            },
        }));

    const handleStateFormChange = (field: string, value: any) => {
        abstractFormInstance.setState = (newState) => setState((prevState) => ({ ...prevState, ...newState }));
        abstractFormInstance.handleStateFormChange(field, value);
    };

    const getStateFormHandlers = () => {
        return {
            ph1EncrypAlgo: {
                value: state.stateForm.ph1EncrypAlgo,
                validation: formModel.fields.ph1EncrypAlgo?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph1EncrypAlgo', event.target.value);
                },
            },
            ph1PRFAlgo: {
                value: state.stateForm.ph1PRFAlgo,
                validation: formModel.fields.ph1PRFAlgo?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph1PRFAlgo', event.target.value);
                },
            },
            ph1DHGroup: {
                value: state.stateForm.ph1DHGroup,
                validation: formModel.fields.ph1DHGroup?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph1DHGroup', event.target.value);
                },
            },
            ph1TimeoutSec: {
                value: state.stateForm.ph1TimeoutSec,
                validation: formModel.fields.ph1TimeoutSec?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph1TimeoutSec', event.target.value);
                },
            },
            ph1IKE: {
                value: state.stateForm.ph1IKE,
                validation: formModel.fields.ph1IKE?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph1IKE', event.target.value);
                },
            },
            ph2ESPEncryp: {
                value: state.stateForm.ph2ESPEncryp,
                validation: formModel.fields.ph2ESPEncryp?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2ESPEncryp', event.target.value);
                },
            },
            ph2DHGroup: {
                value: state.stateForm.ph2DHGroup,
                validation: formModel.fields.ph2DHGroup?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2DHGroup', event.target.value);
                },
            },
            ph2TimeoutSec: {
                value: state.stateForm.ph2TimeoutSec,
                validation: formModel.fields.ph2TimeoutSec?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2TimeoutSec', event.target.value);
                },
            },
            ph2TimeoutKb: {
                value: state.stateForm.ph2TimeoutKb,
                validation: formModel.fields.ph2TimeoutKb?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2TimeoutKb', event.target.value);
                },
            },
            ph2Authentication: {
                value: state.stateForm.ph2Authentication,
                validation: formModel.fields.ph2Authentication?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2Authentication', event.target.value);
                },
            },
            ph2EnablePFS: {
                value: state.stateForm.ph2EnablePFS,
                validation: formModel.fields.ph2EnablePFS?.validation,
                onChange: (event) => {
                    handleStateFormChange('ph2EnablePFS', event.target.value);
                },
            },
        };
    };


    const isValid = () => {
        return true;
        return !formModel && formModel.isValid();
    };

    return {
        state,
        formModel,
        setState,
        getStateFormHandlers,
        isValid,
    };
};